// /health/inbody/:id — карточка замера InBody: все поля, нормы со статусами, сегменты, условия;
// «Исходный PDF», «Изменить» (та же форма, что на сверке), «Удалить» с «Отменить» (откат журнала).

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Badge, Button, Card, ErrorText, Icon, PageSpinner, useToast, type BadgeTone } from '@/components/ui'
import { formatDate, formatDateTime, formatNumber } from '@/lib/format'

import { inbodyApi, inbodyKeys, NORM_KEYS, SEGMENTS, useInvalidateInBody, useScan, type ScanOut, type Status } from './api'
import { SEGMENT_KINDS, toForm, type ScanPayload } from './formState'
import {
  fastedLabel,
  FIELD_GROUPS,
  FIELD_LABELS,
  NORM_LABELS,
  relationLabel,
  SEGMENT_KIND_LABELS,
  SEGMENT_LABELS,
  SEX_LABELS,
  STATUS_LABELS,
} from './labels'
import { AnalysisCard } from './analysis/AnalysisCard'
import { ScanForm } from './ScanForm'

const STATUS_TONES: Record<Status, BadgeTone> = { below: 'warning', normal: 'accent', above: 'warning' }

const linkButton =
  'inline-flex min-h-11 items-center gap-2 rounded-lg border border-border bg-surface px-4 text-sm font-medium text-fg hover:bg-surface-2 lg:min-h-10'

function value(v: number | null | undefined, digits: number, unit: string | null) {
  if (v === null || v === undefined) return <span className="text-muted">—</span>
  return (
    <>
      {formatNumber(v, { digits })}
      {unit ? <span className="font-normal text-muted"> {unit}</span> : null}
    </>
  )
}

function ScanView({ scan }: { scan: ScanOut }) {
  return (
    <div className="flex flex-col gap-4">
      <AnalysisCard scanId={scan.id} analysis={scan.analysis} />
      {FIELD_GROUPS.map((group) => (
        <Card key={group.id} title={group.title}>
          <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 text-sm sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto]">
            {group.fields.map((f) => (
              <div key={f} className="contents">
                <dt className="text-muted">{FIELD_LABELS[f].label}</dt>
                <dd className="text-right font-semibold tabular-nums text-fg">
                  {value(scan[f], FIELD_LABELS[f].digits, FIELD_LABELS[f].unit)}
                </dd>
              </div>
            ))}
            {group.id === 'main' ? (
              <>
                <dt className="text-muted">Возраст</dt>
                <dd className="text-right tabular-nums">{value(scan.age, 0, 'лет')}</dd>
                <dt className="text-muted">Рост</dt>
                <dd className="text-right tabular-nums">{value(scan.height_cm, 1, 'см')}</dd>
                <dt className="text-muted">Пол</dt>
                <dd className="text-right">{scan.sex ? SEX_LABELS[scan.sex] : '—'}</dd>
              </>
            ) : null}
          </dl>
        </Card>
      ))}

      <Card title="Нормы" flush>
        {Object.keys(scan.norms ?? {}).length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted">Норм в замере нет.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-2 text-left text-xs text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Показатель</th>
                  <th className="px-2 py-2 text-right font-medium">Значение</th>
                  <th className="px-2 py-2 text-right font-medium">Норма</th>
                  <th className="px-4 py-2 font-medium">Статус</th>
                </tr>
              </thead>
              <tbody>
                {NORM_KEYS.filter((k) => scan.norms?.[k]).map((k) => {
                  const norm = scan.norms![k]!
                  const status = scan.statuses[k]
                  const label = FIELD_LABELS[k]
                  return (
                    <tr key={k} className="border-t border-border">
                      <td className="px-4 py-2">{NORM_LABELS[k]}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{value(scan[k], label.digits, label.unit)}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-muted">
                        {formatNumber(norm.low, { digits: label.digits })}–{formatNumber(norm.high, { digits: label.digits })}
                      </td>
                      <td className="px-4 py-2">
                        {status ? <Badge tone={STATUS_TONES[status]}>{STATUS_LABELS[status]}</Badge> : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Сегменты" flush>
        {!scan.segments ? (
          <p className="px-4 pb-4 text-sm text-muted">Сегментов в замере нет.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-2 text-left text-xs text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Сегмент</th>
                  {SEGMENT_KINDS.map((kind) => (
                    <th key={kind} className="px-4 py-2 text-right font-medium">
                      {SEGMENT_KIND_LABELS[kind]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SEGMENTS.map((seg) => (
                  <tr key={seg} className="border-t border-border">
                    <td className="px-4 py-2">{SEGMENT_LABELS[seg]}</td>
                    {SEGMENT_KINDS.map((kind) => {
                      const v = scan.segments?.[kind]?.[seg]
                      return (
                        <td key={kind} className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                          {v ? (
                            <>
                              {formatNumber(v.kg, { digits: 2 })} кг
                              {v.pct_of_norm !== null && v.pct_of_norm !== undefined ? (
                                <span className="text-muted"> · {formatNumber(v.pct_of_norm, { digits: 1 })} %</span>
                              ) : null}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Условия замера">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">Время</dt>
          <dd>{formatDateTime(scan.measured_at)}</dd>
          <dt className="text-muted">Питание</dt>
          <dd>{fastedLabel(scan.fasted)}</dd>
          <dt className="text-muted">Тренировка</dt>
          <dd>{relationLabel(scan.workout_relation)}</dd>
          {scan.note ? (
            <>
              <dt className="text-muted">Заметка</dt>
              <dd className="whitespace-pre-wrap">{scan.note}</dd>
            </>
          ) : null}
        </dl>
      </Card>
    </div>
  )
}

function ScanEdit({ scan, onDone }: { scan: ScanOut; onDone: () => void }) {
  const toast = useToast()
  const client = useQueryClient()
  const invalidate = useInvalidateInBody()
  const save = useMutation({
    mutationFn: (payload: ScanPayload) => {
      const { import_history: _skip, ...patch } = payload
      return inbodyApi.updateScan(scan.id, patch)
    },
    networkMode: 'always',
    onSuccess: (updated) => {
      client.setQueryData(inbodyKeys.scan(updated.id), updated)
      void invalidate()
      toast.show({ message: 'Замер исправлен', tone: 'accent' })
      onDone()
    },
  })
  return (
    <ScanForm
      initial={toForm(scan, scan)}
      pending={save.isPending}
      error={save.error}
      onSubmit={(payload) => save.mutate(payload)}
      onCancel={onDone}
    />
  )
}

export function ScanPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateInBody()
  const scan = useScan(id)
  const [editing, setEditing] = useState(false)

  const undo = useMutation({
    mutationFn: inbodyApi.undo,
    networkMode: 'always',
    onSuccess: () => {
      void invalidate()
      toast.show({ message: 'Замер возвращён' })
      navigate(`/health/inbody/${id}`)
    },
    onError: (error) =>
      toast.show({ message: error instanceof ApiError ? error.message : 'Не получилось отменить', tone: 'danger' }),
  })
  const remove = useMutation({
    mutationFn: () => inbodyApi.deleteScan(id),
    networkMode: 'always',
    onSuccess: ({ change_id }) => {
      void invalidate()
      toast.show({
        message: 'Замер удалён',
        action: { label: 'Отменить', onClick: () => undo.mutate(change_id) },
        duration: 8000,
      })
      navigate('/health/inbody', { replace: true })
    },
    onError: (error) =>
      toast.show({ message: error instanceof ApiError ? error.message : 'Не удалось удалить', tone: 'danger' }),
  })

  const back = (
    <Link to="/health/inbody" className="-ml-1 mb-2 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-fg lg:min-h-8">
      <Icon name="chevron-left" size={18} />
      Состав тела
    </Link>
  )
  if (scan.isPending) return <PageSpinner />
  if (scan.isError) {
    const notFound = scan.error instanceof ApiError && scan.error.status === 404
    return (
      <div className="flex flex-col">
        {back}
        <ErrorText error={notFound ? null : scan.error}>{notFound ? 'Замер не найден — возможно, он удалён.' : undefined}</ErrorText>
      </div>
    )
  }
  const data = scan.data
  return (
    <div className="flex flex-col">
      {back}
      <div className="mb-4 flex flex-wrap items-end gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-fg">
            Замер InBody за {formatDate(data.day)}
            {!data.complete ? <Badge>неполный</Badge> : null}
          </h2>
          <p className="mt-0.5 text-sm text-muted">
            {formatDateTime(data.measured_at)} · {fastedLabel(data.fasted)} · {relationLabel(data.workout_relation)}
          </p>
        </div>
        {!editing ? (
          <div className="flex flex-wrap gap-2">
            {data.has_pdf ? (
              <a href={inbodyApi.pdfUrl(data.id)} download className={linkButton}>
                <Icon name="download" size={18} />
                Исходный PDF
              </a>
            ) : null}
            <Button onClick={() => setEditing(true)}>Изменить</Button>
            <Button variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
              Удалить
            </Button>
          </div>
        ) : null}
      </div>
      {editing ? <ScanEdit scan={data} onDone={() => setEditing(false)} /> : <ScanView scan={data} />}
    </div>
  )
}
